import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { setRouteCanonical, clearRouteCanonical, toAbsoluteCanonical } from './Canonical';
import { buildProductUrl } from '../lib/productUrl';

const SITE_NAME = 'Biscuit Brand';
const CANONICAL_ORIGIN = (process.env.REACT_APP_CANONICAL_URL || 'https://biscuitbrand.com').replace(/\/+$/, '');
const SITE_URL = CANONICAL_ORIGIN;
const DEFAULT_IMAGE = `${SITE_URL}/favicon-512x512.png`;
const DEFAULT_DESCRIPTION = 'Biscuit Brand is the marketplace dedicated entirely to biscuits. Discover biscuit brands, biscuit manufacturers, artisan biscuit makers, biscuit gifts, and biscuits from around the world.';

// The global <Canonical /> component (mounted once in App.js) emits a
// self-referencing canonical based on location.pathname for every route.
// When SEO is given an explicit `canonical` prop (e.g. a product page that
// must canonical to its slug URL even when reached via the ID URL), we
// register a per-route override via setRouteCanonical(). The override is
// auto-cleared on the next route change.
const SEO = ({
    title,
    description,
    image,
    type = 'website',
    noindex = false,
    jsonLd,
    children,
    canonical,
}) => {
    useEffect(() => {
        if (!canonical) return undefined;
        const abs = toAbsoluteCanonical(canonical);
        if (!abs) return undefined;
        setRouteCanonical(abs);
        return () => clearRouteCanonical();
    }, [canonical]);

    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — The Global Biscuit Marketplace`;
    const metaDesc = description || DEFAULT_DESCRIPTION;
    const metaImage = image || DEFAULT_IMAGE;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={metaDesc} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDesc} />
            <meta property="og:type" content={type} />
            <meta property="og:image" content={metaImage} />
            <meta property="og:site_name" content={SITE_NAME} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDesc} />
            <meta name="twitter:image" content={metaImage} />

            {/* JSON-LD Structured Data */}
            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
            {children}
        </Helmet>
    );
};

// Structured data helpers
export const organizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon-512x512.png`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [],
});

export const websiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/shop?search={search_term_string}`,
        'query-input': 'required name=search_term_string',
    },
});

export const productSchema = (product, sellerName) => {
    const canonicalUrl = `${SITE_URL}${buildProductUrl(product)}`;
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description?.substring(0, 300),
        image: product.images?.[0],
        url: canonicalUrl,
        brand: {
            '@type': 'Brand',
            name: product.brand || sellerName || SITE_NAME,
        },
        offers: {
            '@type': 'Offer',
            price: product.price,
            priceCurrency: 'GBP',
            url: canonicalUrl,
            availability: product.stock > 0
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
            seller: {
                '@type': 'Organization',
                name: sellerName || SITE_NAME,
            },
        },
    };
    if (product.rating > 0 && product.review_count > 0) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: product.rating,
            reviewCount: product.review_count,
        };
    }
    return schema;
};

export const breadcrumbSchema = (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        ...(item.url ? { item: `${SITE_URL}${item.url}` } : {}),
    })),
});

export const sellerSchema = (seller) => ({
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: seller.store_name,
    description: seller.seo_description || seller.store_description?.substring(0, 300),
    url: `${SITE_URL}/store/${seller.slug || seller.seller_id}`,
    ...(seller.store_logo ? { image: seller.store_logo } : {}),
    ...(seller.rating > 0 && seller.review_count > 0 ? {
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: seller.rating,
            reviewCount: seller.review_count,
        },
    } : {}),
});

export default SEO;
